import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, MessageSquare, HeadphonesIcon, Globe, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import FAQ from '../components/FAQ';
import Container from '../components/common/Container';

const SupportCard = ({ icon: Icon, title, description, linkText, delay, linkTo }) => (
    <Link to={linkTo} className="block outline-none h-full">
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay }}
            whileHover={{ y: -5 }}
            className="bg-white p-8 rounded-2xl border border-primary/10 shadow-lg hover:shadow-xl hover:border-secondary/30 transition-all duration-300 group h-full cursor-pointer flex flex-col"
        >
            <div className="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center mb-6 text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-300 shrink-0">
                <Icon size={28} />
            </div>
            <h3 className="text-2xl font-serif font-bold text-primary mb-3">{title}</h3>
            <p className="text-secondary mb-6 font-sans text-md leading-relaxed flex-grow font-medium">{description}</p>
            <div className="text-secondary font-bold text-sm tracking-wide uppercase flex items-center gap-2 group-hover:text-primary transition-colors mt-auto">
                {linkText} <span className="text-lg group-hover:translate-x-1 transition-transform">→</span>
            </div>
        </motion.div>
    </Link>
);

const SupportPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-background overflow-hidden selection:bg-secondary selection:text-white">

            {/* Hero Section */}
            <section className="relative py-20 lg:py-28 overflow-hidden bg-white pt-32 lg:pt-40">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/5 blur-[120px] rounded-full pointer-events-none" />
                <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

                <Container className="relative z-10 text-center max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        {/* <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs mb-4 block">Help Center</span> */}
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-primary mb-6 leading-tight">
                            How can we <span className="">help you?</span>
                        </h1>
                        <p className="text-lg md:text-xl text-secondary font-sans max-w-2xl mx-auto mb-10">
                            Find answers to your questions, connect with our team, and learn more about our real estate projects and developments.
                        </p>
                    </motion.div>
                </Container>
            </section>

            {/* Support Options Grid */}
            <section className="py-10 bg-background relative z-20 -mt-10 lg:-mt-20">
                <Container>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <SupportCard
                            icon={MessageSquare}
                            title="General Inquiries"
                            description="Have questions about our ongoing or upcoming projects? We are here to provide all the information you need."
                            linkText="Contact Us"
                            linkTo="/contact"
                            delay={0.1}
                        />
                        <SupportCard
                            icon={Globe}
                            title="Explore Projects"
                            description="Browse through our comprehensive portfolio of premium residential and commercial developments."
                            linkText="View Portfolio"
                            linkTo="/projects"
                            delay={0.2}
                        />
                        <SupportCard
                            icon={FileText}
                            title="Project Gallery"
                            description="Find detailed insights into our properties, including locations, amenities, and architectural highlights."
                            linkText="Learn More"
                            linkTo="/gallery"
                            delay={0.3}
                        />
                    </div>
                </Container>
            </section>

            {/* FAQ Section */}
            <FAQ />
        </div>
    );
};

export default SupportPage;
